// frontend/src/pages/Clients/ClientsList.jsx
// ✅ MEJORADO: Datos vinculados correctamente + navegación a detalle

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ProtectedComponent from '../../components/ProtectedComponent';
import { PERMISSIONS } from '../../utils/permissions';
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Button,
  Table,
  Badge,
  Input,
  InputGroup,
  InputGroupText,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import { clientsAPI } from '../../services/api';

function ClientsList() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    loadClients();
  }, [statusFilter]);

  const loadClients = async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      
      const response = await clientsAPI.getAll(params);
      const data = response.data.results || response.data;
      setClients(Array.isArray(data) ? data : []);
      setError(null);
    } catch (error) {
      console.error('Error al cargar clientes:', error);
      setError('No se pudieron cargar los clientes. Verifica que el backend esté corriendo.');
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClientClick = (clientId) => {
    navigate(`/admin/clients/${clientId}`);
  };

  const filteredClients = clients.filter((client) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      client.name.toLowerCase().includes(searchLower) ||
      client.client_id.toLowerCase().includes(searchLower) ||
      (client.rfc && client.rfc.toLowerCase().includes(searchLower)) ||
      (client.email && client.email.toLowerCase().includes(searchLower)) ||
      (client.account_manager_display && client.account_manager_display.toLowerCase().includes(searchLower))
    );
  });

  // Estadísticas
  const totalClients = clients.length;
  const activeClients = clients.filter((c) => c.status === 'active').length;
  const totalSales = clients.reduce((sum, c) => sum + (c.total_sales || 0), 0);
  const totalQuotes = clients.reduce((sum, c) => sum + (c.total_quotes || 0), 0);
  const totalOrders = clients.reduce((sum, c) => sum + (c.total_orders || 0), 0);

  if (loading) {
    return (
      <>
        <div className="min-height-300 bg-gradient-primary position-absolute w-100"></div>
        <Container fluid className="py-4">
          <div className="text-center mt-5">
            <div className="spinner-border text-white" role="status">
              <span className="visually-hidden">Cargando...</span>
            </div>
            <p className="mt-3 text-white">Cargando clientes...</p>
          </div>
        </Container>
      </>
    );
  }

  return (
    <>
      <div className="min-height-300 bg-gradient-primary position-absolute w-100"></div>

      <Container fluid className="py-4">
        {error && (
          <Row>
            <Col>
              <div className="alert alert-danger alert-dismissible fade show shadow-sm" role="alert">
                <span className="alert-icon">
                  <i className="ni ni-support-16"></i>
                </span>
                <span className="alert-text">
                  <strong>Error:</strong> {error}
                </span>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setError(null)}
                  aria-label="Close"
                ></button>
              </div>
            </Col>
          </Row>
        )}

        {/* Stats Cards */}
        <Row>
          <Col xl="3" sm="6" className="mb-xl-0 mb-4">
            <Card className="shadow border-0">
              <CardBody className="p-3">
                <Row>
                  <div className="col-8">
                    <div className="numbers">
                      <p className="text-sm mb-0 text-uppercase font-weight-bold text-muted">
                        Total Clientes
                      </p>
                      <h5 className="font-weight-bolder mb-0 text-dark">{totalClients}</h5>
                      <p className="mb-0">
                        <span className="text-success text-sm font-weight-bolder">
                          {activeClients} activos
                        </span>
                      </p>
                    </div>
                  </div>
                  <div className="col-4 text-end">
                    <div className="icon icon-shape bg-gradient-primary shadow-primary text-center rounded-circle">
                      <i className="ni ni-briefcase-24 text-lg opacity-10 text-white"></i>
                    </div>
                  </div>
                </Row>
              </CardBody>
            </Card>
          </Col>

          <Col xl="3" sm="6" className="mb-xl-0 mb-4">
            <Card className="shadow border-0">
              <CardBody className="p-3">
                <Row>
                  <div className="col-8">
                    <div className="numbers">
                      <p className="text-sm mb-0 text-uppercase font-weight-bold text-muted">
                        Propuestas
                      </p>
                      <h5 className="font-weight-bolder mb-0 text-dark">{totalQuotes}</h5>
                      <p className="mb-0">
                        <span className="text-info text-sm font-weight-bolder">
                          {totalClients > 0 ? (totalQuotes / totalClients).toFixed(1) : 0}
                        </span>
                        <span className="text-xs text-muted"> promedio</span>
                      </p>
                    </div>
                  </div>
                  <div className="col-4 text-end">
                    <div className="icon icon-shape bg-gradient-info shadow-info text-center rounded-circle">
                      <i className="ni ni-single-copy-04 text-lg opacity-10 text-white"></i>
                    </div>
                  </div>
                </Row>
              </CardBody>
            </Card>
          </Col>

          <Col xl="3" sm="6" className="mb-xl-0 mb-4">
            <Card className="shadow border-0">
              <CardBody className="p-3">
                <Row>
                  <div className="col-8">
                    <div className="numbers">
                      <p className="text-sm mb-0 text-uppercase font-weight-bold text-muted">
                        Órdenes
                      </p>
                      <h5 className="font-weight-bolder mb-0 text-dark">{totalOrders}</h5>
                      <p className="mb-0">
                        <span className="text-success text-sm font-weight-bolder">
                          {totalQuotes > 0 ? ((totalOrders / totalQuotes) * 100).toFixed(1) : 0}%
                        </span>
                        <span className="text-xs text-muted"> conversión</span>
                      </p>
                    </div>
                  </div>
                  <div className="col-4 text-end">
                    <div className="icon icon-shape bg-gradient-success shadow-success text-center rounded-circle">
                      <i className="ni ni-bag-17 text-lg opacity-10 text-white"></i>
                    </div>
                  </div>
                </Row>
              </CardBody>
            </Card>
          </Col>

          <Col xl="3" sm="6">
            <Card className="shadow border-0">
              <CardBody className="p-3">
                <Row>
                  <div className="col-8">
                    <div className="numbers">
                      <p className="text-sm mb-0 text-uppercase font-weight-bold text-muted">
                        Ventas Totales
                      </p>
                      <h5 className="font-weight-bolder mb-0 text-dark">
                        ${totalSales.toLocaleString('es-MX', { maximumFractionDigits: 0 })}
                      </h5>
                      <p className="mb-0">
                        <span className="text-warning text-sm font-weight-bolder">
                          ${totalOrders > 0 ? (totalSales / totalOrders).toLocaleString('es-MX', { maximumFractionDigits: 0 }) : 0}
                        </span>
                        <span className="text-xs text-muted"> promedio</span>
                      </p>
                    </div>
                  </div>
                  <div className="col-4 text-end">
                    <div className="icon icon-shape bg-gradient-warning shadow-warning text-center rounded-circle">
                      <i className="ni ni-money-coins text-lg opacity-10 text-white"></i>
                    </div>
                  </div>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Tabla de clientes */}
        <Row className="mt-4">
          <Col lg="12">
            <Card className="shadow border-0 mb-4">
              <CardHeader className="bg-gradient-primary pb-3">
                <Row className="align-items-center">
                  <Col md="6">
                    <h6 className="mb-0 text-white">
                      <i className="ni ni-briefcase-24 me-2"></i>
                      Lista de Clientes
                    </h6>
                  </Col>
                  <Col md="6" className="text-end">
                    <ProtectedComponent permission={PERMISSIONS.CREATE_CLIENTS}>
                      <Button 
                        color="white" 
                        size="sm"
                        className="btn-icon shadow-sm"
                      >
                        <span className="btn-inner--icon">
                          <i className="ni ni-fat-add"></i>
                        </span>
                        <span className="btn-inner--text ms-2">Nuevo Cliente</span>
                      </Button>
                    </ProtectedComponent>
                  </Col>
                </Row>
                
                {/* Filtros */}
                <Row className="mt-3">
                  <Col md="6">
                    <InputGroup className="input-group-alternative shadow-sm">
                      <InputGroupText className="bg-white">
                        <i className="fas fa-search text-muted"></i>
                      </InputGroupText>
                      <Input
                        type="text"
                        className="form-control-alternative"
                        placeholder="Buscar por nombre, ID, RFC, email o account manager..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </InputGroup>
                  </Col>
                  <Col md="3">
                    <UncontrolledDropdown>
                      <DropdownToggle
                        caret
                        color="white"
                        className="w-100 shadow-sm"
                        size="sm"
                      >
                        <i className="ni ni-bullet-list-67 me-2"></i>
                        {statusFilter === 'all' ? 'Todos' : statusFilter === 'active' ? 'Activos' : 'Inactivos'}
                      </DropdownToggle>
                      <DropdownMenu>
                        <DropdownItem onClick={() => setStatusFilter('all')}>
                          Todos
                        </DropdownItem>
                        <DropdownItem onClick={() => setStatusFilter('active')}>
                          Activos
                        </DropdownItem>
                        <DropdownItem onClick={() => setStatusFilter('inactive')}>
                          Inactivos
                        </DropdownItem>
                      </DropdownMenu>
                    </UncontrolledDropdown>
                  </Col>
                  <Col md="3" className="text-end">
                    <Button
                      color="white"
                      size="sm"
                      className="shadow-sm"
                      onClick={loadClients}
                    >
                      <i className="ni ni-refresh-02 me-2"></i>
                      Actualizar
                    </Button>
                  </Col>
                </Row>
              </CardHeader>

              <CardBody className="px-0 pt-0 pb-2">
                <div className="table-responsive p-0">
                  <Table className="align-items-center mb-0" hover>
                    <thead>
                      <tr>
                        <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                          Cliente
                        </th>
                        <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">
                          Account Manager
                        </th>
                        <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                          Contacto
                        </th>
                        <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                          Propuestas
                        </th>
                        <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                          Órdenes
                        </th>
                        <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                          Ventas
                        </th>
                        <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                          Estado
                        </th>
                        <th className="text-secondary opacity-7"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredClients.length === 0 ? (
                        <tr>
                          <td colSpan="8" className="text-center py-5">
                            <i className="ni ni-briefcase-24 text-muted" style={{ fontSize: '3rem' }}></i>
                            <p className="text-xs text-secondary mb-0 mt-3">
                              {searchTerm
                                ? 'No se encontraron clientes con ese criterio'
                                : 'No hay clientes registrados. Agrega uno para comenzar.'}
                            </p>
                          </td>
                        </tr>
                      ) : (
                        filteredClients.map((client) => (
                          <tr 
                            key={client.id} 
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleClientClick(client.id)}
                          >
                            <td>
                              <div className="d-flex px-2 py-1">
                                <div>
                                  <div className="icon icon-shape icon-sm bg-gradient-primary shadow text-center rounded-circle me-2">
                                    <span className="text-white text-xs font-weight-bold">
                                      {client.name.charAt(0)}
                                    </span>
                                  </div>
                                </div>
                                <div className="d-flex flex-column justify-content-center">
                                  <h6 className="mb-0 text-sm text-dark">{client.name}</h6>
                                  <p className="text-xs text-secondary mb-0">
                                    <i className="ni ni-badge me-1"></i>
                                    {client.client_id}
                                  </p>
                                </div>
                              </div>
                            </td>
                            <td>
                              <p className="text-sm font-weight-bold mb-0 text-dark">
                                {client.account_manager_display || 'Sin asignar'}
                              </p>
                                                            {client.email && (
                                <p className="text-xs text-secondary mb-0">
                                  <i className="ni ni-email-83 me-1"></i>
                                  {client.email}
                                </p>
                              )}
                            </td>
                            <td className="align-middle text-center">
                              {client.primary_contact ? (
                                <>
                                  <span className="text-dark text-xs font-weight-bold">
                                    {client.primary_contact.name}
                                  </span>
                                  {client.primary_contact.email && (
                                    <p className="text-xs text-secondary mb-0">
                                      <i className="ni ni-email-83 me-1"></i>
                                      {client.primary_contact.email}
                                    </p>
                                  )}
                                </>
                              ) : (
                                <span className="text-xs text-muted">Sin contacto</span>
                              )}
                            </td>
                            <td className="align-middle text-center">
                              <Badge color="" className="badge-sm bg-gradient-info shadow-sm">
                                {client.total_quotes || 0}
                              </Badge>
                            </td>
                            <td className="align-middle text-center">
                              <Badge color="" className="badge-sm bg-gradient-success shadow-sm">
                                {client.total_orders || 0}
                              </Badge>
                            </td>
                            <td className="align-middle text-center">
                              <span className="text-dark text-sm font-weight-bold">
                                ${(client.total_sales || 0).toLocaleString('es-MX', { maximumFractionDigits: 0 })}
                              </span>
                            </td>
                            <td className="align-middle text-center text-sm">
                              {client.status === 'active' ? (
                                <Badge color="" className="badge-sm bg-gradient-success shadow-sm">
                                  <i className="ni ni-check-bold me-1"></i>
                                  Activo
                                </Badge>
                              ) : (
                                <Badge color="" className="badge-sm bg-gradient-secondary shadow-sm">
                                  <i className="ni ni-fat-remove me-1"></i>
                                  Inactivo
                                </Badge>
                              )}
                            </td>
                            <td className="align-middle text-center">
                              
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default ClientsList;
